import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { api } from "../../../lib/api";
import { useDebounce } from "../../../hooks/useDebounce";
import { TemplateDesigner } from "./TemplateDesigner";
import { PdfViewerModal } from "./PdfViewerModal";
import type { ContractField } from "../../../types";

type Template = {
  id: number;
  name: string;
  description: string;
  category: string;
  tags: string[];
  is_active: boolean;
  times_used: number;
  last_used_date: string | null;
  published_version: number;
  legacy_unversioned: boolean;
  template_fields: ContractField[];
  pdf_url?: string;
  field_mappings: Record<string, { source: string; default?: string }>;
  default_recipients: { role: string }[];
  use_signing_order: boolean;
  link_expiration_days: number;
};
type Workflow = {
  readiness: {
    ready: boolean;
    issues: string[];
    fields: {
      id: string;
      label: string;
      page: number;
      signer: string;
      issues: string[];
    }[];
  };
  versions: { id: number; number: number; created_at: string }[];
  has_unpublished_changes: boolean;
  sources: string[];
  layouts: { id: number; name: string }[];
  recent_contracts: {
    id: number;
    contract_number: string;
    status: string;
    source_template_version__number: number;
  }[];
};
const errorText = (e: any) =>
  e?.responseData?.error ||
  e?.responseData?.detail ||
  (e?.responseData ? JSON.stringify(e.responseData) : e.message) ||
  "Action failed. Please try again.";
const initialSample = {
  "contact.name": "Taylor Morgan",
  "contact.first_name": "Taylor",
  "contact.last_name": "Morgan",
  "contact.email": "taylor@example.test",
  "contact.phone": "555-0100",
  "opportunity.name": "Sample roof replacement",
  "opportunity.value": "15400",
  "organization.name": "Sample Roofing",
  "salesperson.name": "Sam Ellis",
  "salesperson.email": "sam@example.test",
};

function TemplateReview({ id, close }: { id: number; close: () => void }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [layoutName, setLayoutName] = useState(""),
    [layout, setLayout] = useState<number | null>(null),
    [maps, setMaps] = useState<Template["field_mappings"]>({}),
    [sample, setSample] = useState<Record<string, string>>(initialSample),
    [preview, setPreview] = useState<any>(null);
  const template = useQuery({
    queryKey: ["template", id],
    queryFn: async () =>
      (await api.get<Template>(`/api/crm/contract-templates/${id}/`)).data,
  });
  const workflow = useQuery({
    queryKey: ["template-workflow", id],
    queryFn: async () =>
      (await api.get<Workflow>(`/api/crm/contract-templates/${id}/workflow/`))
        .data,
  });
  useEffect(() => {
    if (template.data) setMaps(template.data.field_mappings || {});
  }, [template.data]);
  const run = async (name: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(name);
    setError("");
    try {
      await fn();
      await Promise.all([
        client.invalidateQueries({ queryKey: ["templates"] }),
        template.refetch(),
        workflow.refetch(),
      ]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy("");
    }
  };
  const w = workflow.data,
    t = template.data;
  return (
    <Modal
      open
      onClose={() => {
        if (!busy) close();
      }}
    >
      <ModalDialog
        sx={{ width: "min(1000px,95vw)", maxHeight: "94vh", overflow: "auto" }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h3">{t?.name || "Template review"}</Typography>
          <Button variant="plain" disabled={!!busy} onClick={close}>
            Close
          </Button>
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        {template.isError || workflow.isError ? (
          <Alert color="danger">
            Review unavailable.{" "}
            <Button
              onClick={() => {
                template.refetch();
                workflow.refetch();
              }}
            >
              Retry
            </Button>
          </Alert>
        ) : !w || !t ? (
          <CircularProgress />
        ) : (
          <>
            <Alert color={w.has_unpublished_changes ? "warning" : "success"}>
              {t.published_version
                ? `Published version ${t.published_version}. ${w.has_unpublished_changes ? "Draft changes are not used until published." : "Draft matches the published version."}`
                : "No published version yet."}
            </Alert>
            <Typography level="title-lg">Readiness</Typography>
            {w.readiness.issues.map((issue) => (
              <Alert color="warning" key={issue}>
                {issue}
              </Alert>
            ))}
            {w.readiness.fields.map((f) => (
              <Card key={f.id}>
                <Typography>
                  {f.label} · Page {f.page} · {f.signer}
                </Typography>
                {f.issues.map((issue) => (
                  <Typography color="danger" key={issue}>
                    {issue}
                  </Typography>
                ))}
              </Card>
            ))}
            <Button
              disabled={
                !!busy || !w.readiness.ready || !w.has_unpublished_changes
              }
              loading={busy === "publish"}
              onClick={() =>
                run("publish", () =>
                  api.post(`/api/crm/contract-templates/${id}/publish/`, {}),
                )
              }
            >
              Publish ready version
            </Button>
            <Typography level="title-lg">Field mapping</Typography>
            <Typography level="body-sm">
              Choose a record value or a default. Empty mapped values are
              highlighted in preview and block contract creation.
            </Typography>
            {(t.template_fields || [])
              .filter((f) => !["signature", "initial"].includes(f.type))
              .map((f) => (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  gap={1}
                  key={f.id}
                >
                  <Typography sx={{ minWidth: 150 }}>
                    {f.label || f.type}
                  </Typography>
                  <Select
                    aria-label={`Source for ${f.label || f.id}`}
                    value={maps[f.id]?.source || ""}
                    onChange={(_, source) =>
                      setMaps((old) => {
                        const next = { ...old };
                        if (source) next[f.id] = { ...next[f.id], source };
                        else delete next[f.id];
                        return next;
                      })
                    }
                    sx={{ flex: 1 }}
                  >
                    <Option value="">Signer enters value</Option>
                    <Option value="default">Default only</Option>
                    {w.sources.map((source) => (
                      <Option value={source} key={source}>
                        {source}
                      </Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="Fallback value"
                    value={maps[f.id]?.default || ""}
                    disabled={!maps[f.id]}
                    onChange={(e) =>
                      setMaps((old) => ({
                        ...old,
                        [f.id]: { ...old[f.id], default: e.target.value },
                      }))
                    }
                  />
                </Stack>
              ))}
            <Button
              variant="outlined"
              disabled={!!busy}
              onClick={() =>
                run("mapping", () =>
                  api.patch(`/api/crm/contract-templates/${id}/`, {
                    field_mappings: maps,
                  }),
                )
              }
            >
              Save mappings to draft
            </Button>
            <Typography level="title-lg">Reusable field layouts</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Input
                placeholder="Name this layout"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
              />
              <Button
                disabled={!!busy || !layoutName.trim()}
                onClick={() =>
                  run("layout", () =>
                    api.post(`/api/crm/contract-templates/${id}/save-layout/`, {
                      name: layoutName,
                    }),
                  )
                }
              >
                Save layout
              </Button>
              <Select
                placeholder="Saved layout"
                value={layout}
                onChange={(_, value) => setLayout(value)}
              >
                {w.layouts.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              <Button
                disabled={!!busy || !layout}
                variant="outlined"
                onClick={() =>
                  run("apply", () =>
                    api.post(
                      `/api/crm/contract-templates/${id}/apply-layout/`,
                      { layout_id: layout },
                    ),
                  )
                }
              >
                Replace draft fields with layout
              </Button>
            </Stack>
            <Typography level="title-lg">Sample customer preview</Typography>
            <Typography level="body-sm">
              Uses sample values and the saved draft without creating a
              contract.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
              }}
            >
              {Object.keys(initialSample).map((key) => (
                <FormControl key={key}>
                  <FormLabel>{key}</FormLabel>
                  <Input
                    value={sample[key]}
                    onChange={(e) =>
                      setSample((old) => ({ ...old, [key]: e.target.value }))
                    }
                  />
                </FormControl>
              ))}
            </Box>
            <Button
              disabled={!!busy || !t.pdf_url}
              onClick={() =>
                run("preview", async () => {
                  setPreview(
                    (
                      await api.post(
                        `/api/crm/contract-templates/${id}/sample-preview/`,
                        sample,
                      )
                    ).data,
                  );
                })
              }
            >
              Preview populated draft
            </Button>
            <Typography level="title-lg">
              Published versions and recent use
            </Typography>
            {w.versions.map((version) => (
              <Typography key={version.id}>
                Version {version.number} ·{" "}
                {new Date(version.created_at).toLocaleString()}
              </Typography>
            ))}
            {w.recent_contracts.map((record) => (
              <Typography
                component={Link}
                key={record.id}
                to={`/crm/contracts/${record.id}`}
              >
                {record.contract_number} · Version{" "}
                {record.source_template_version__number} · {record.status}
              </Typography>
            ))}
          </>
        )}
        {preview && (
          <>
            {preview.missing_fields?.length > 0 && (
              <Alert color="warning">
                Missing mapped values: {preview.missing_fields.join(", ")}
              </Alert>
            )}
            <PdfViewerModal
              open
              onClose={() => setPreview(null)}
              pdfUrl={preview.pdf_url}
              title={
                preview.missing_fields?.length
                  ? `Sample preview · Missing: ${preview.missing_fields.join(", ")}`
                  : "Sample customer · draft preview"
              }
              fields={preview.fields}
              fieldValues={preview.values}
            />
          </>
        )}
      </ModalDialog>
    </Modal>
  );
}

export const TemplateManager = ({
  onClose,
  onUseTemplate,
}: {
  onClose?: () => void;
  onUseTemplate?: (id: number) => void;
}) => {
  const client = useQueryClient();
  const [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [active, setActive] = useState(""),
    [tag, setTag] = useState(""),
    [page, setPage] = useState(1);
  const term = useDebounce(search, 250);
  const [review, setReview] = useState<number | null>(null),
    [designer, setDesigner] = useState<number | null>(null),
    [edit, setEdit] = useState<Template | null | false>(false),
    [remove, setRemove] = useState<Template | null>(null),
    [use, setUse] = useState<Template | null>(null);
  const [busy, setBusy] = useState(""),
    [error, setError] = useState("");
  const actionLock = useRef(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "",
    tags: "",
    roles: "customer, sales_rep",
    use_signing_order: false,
    link_expiration_days: 10,
  });
  const [customer, setCustomer] = useState<any>(null),
    [contactSearch, setContactSearch] = useState(""),
    [opportunity, setOpportunity] = useState<any>(null);
  const requestId = useRef(crypto.randomUUID());
  const [categoryModal, setCategoryModal] = useState(false),
    [categoryName, setCategoryName] = useState(""),
    [previousCategory, setPreviousCategory] = useState("");
  const params = new URLSearchParams({
    page: String(page),
    page_size: "20",
    ordering: "name,id",
  });
  if (term) params.set("search", term);
  if (category) params.set("category", category);
  if (active) params.set("is_active", active);
  if (tag) params.set("tag", tag);
  const listing = useQuery({
    queryKey: ["templates", params.toString()],
    queryFn: async () =>
      (
        await api.get<{ count: number; page: number; results: Template[] }>(
          `/api/crm/contract-templates/?${params}`,
        )
      ).data,
  });
  const categories = useQuery({
    queryKey: ["template-categories"],
    queryFn: async () =>
      (
        await api.get<{ categories: string[]; tags: string[] }>(
          `/api/crm/contract-templates/categories/`,
        )
      ).data,
  });
  const contacts = useQuery({
    queryKey: ["template-contacts", contactSearch],
    enabled: !!use,
    queryFn: async () =>
      (
        await api.get<any>(
          `/api/crm/contacts/?search=${encodeURIComponent(contactSearch)}&page_size=30`,
        )
      ).data,
  });
  const opportunities = useQuery({
    queryKey: ["template-opportunities", customer?.id],
    enabled: !!customer && !!use,
    queryFn: async () =>
      (
        await api.get<any>(
          `/api/crm/opportunities/?contact=${customer.id}&page_size=100`,
        )
      ).data,
  });
  useEffect(() => {
    if (listing.data?.page && listing.data.page !== page)
      setPage(listing.data.page);
  }, [listing.data?.page, page]);
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["templates"] }),
      client.invalidateQueries({ queryKey: ["template-categories"] }),
    ]);
  };
  const run = async (name: string, fn: () => Promise<unknown>) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(name);
    setError("");
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(errorText(e));
    } finally {
      actionLock.current = false;
      setBusy("");
    }
  };
  const openEdit = (template: Template | null) => {
    setError("");
    setEdit(template);
    setForm({
      name: template?.name || "",
      description: template?.description || "",
      category: template?.category || "",
      tags: template?.tags?.join(", ") || "",
      roles:
        template?.default_recipients?.map((r) => r.role).join(", ") ||
        "customer, sales_rep",
      use_signing_order: template?.use_signing_order || false,
      link_expiration_days: template?.link_expiration_days || 10,
    });
  };
  const changing = term !== search;
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          {onClose && (
            <Button variant="plain" onClick={onClose}>
              Back to contracts
            </Button>
          )}
          <Typography level="h2">Contract templates</Typography>
          <Typography>
            Prepare a draft, publish a version, and reuse it with confidence.
          </Typography>
        </Box>
        <Button onClick={() => openEdit(null)}>New template</Button>
      </Stack>
      {error && (
        <Alert color="danger" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      <CollapsibleFilters><Card sx={{ my: 2 }}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Input
            placeholder="Search all templates…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flex: "1 1 240px" }}
          />
          <Select
            value={category}
            onChange={(_, v) => {
              setCategory(v || "");
              setPage(1);
            }}
          >
            <Option value="">All categories</Option>
            {categories.data?.categories.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
          <Select
            value={active}
            onChange={(_, v) => {
              setActive(v || "");
              setPage(1);
            }}
          >
            <Option value="">Any availability</Option>
            <Option value="true">Active</Option>
            <Option value="false">Inactive</Option>
          </Select>
          <Select
            value={tag}
            onChange={(_, v) => {
              setTag(v || "");
              setPage(1);
            }}
          >
            <Option value="">All tags</Option>
            {categories.data?.tags.map((t) => (
              <Option key={t} value={t}>
                {t}
              </Option>
            ))}
          </Select>
          <Button
            variant="outlined"
            onClick={() => {
              setError("");
              setCategoryModal(true);
            }}
          >
            Manage categories
          </Button>
        </Stack>
        {categories.isError && (
          <Alert color="warning">
            Categories unavailable.{" "}
            <Button onClick={() => categories.refetch()}>Retry</Button>
          </Alert>
        )}
      </Card></CollapsibleFilters>
      {listing.isPending || changing ? (
        <CircularProgress />
      ) : listing.isError ? (
        <Alert color="danger">
          Templates unavailable.{" "}
          <Button onClick={() => listing.refetch()}>Retry</Button>
        </Alert>
      ) : (
        <>
          <Typography sx={{ mb: 1 }}>
            {listing.data?.count || 0} matching templates · Page {page}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2,minmax(0,1fr))",
                xl: "repeat(3,minmax(0,1fr))",
              },
              gap: 2,
            }}
          >
            {listing.data?.results.map((t) => (
              <Card key={t.id}>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip color={t.is_active ? "success" : "neutral"}>
                    {t.is_active ? "Active" : "Inactive"}
                  </Chip>
                  <Chip>
                    {t.published_version
                      ? `Published v${t.published_version}`
                      : t.legacy_unversioned
                        ? "Existing template · version on next use"
                        : "Draft"}
                  </Chip>
                </Stack>
                <Typography level="title-lg">{t.name}</Typography>
                <Typography>{t.description || "No description"}</Typography>
                <Typography level="body-sm">
                  {t.category || "Uncategorized"}
                  {t.tags?.length ? ` · ${t.tags.join(", ")}` : ""}
                </Typography>
                <Typography level="body-sm">
                  Used {t.times_used} times
                  {t.last_used_date
                    ? ` · Last used ${new Date(t.last_used_date).toLocaleDateString()}`
                    : ""}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    onClick={() => {
                      setReview(t.id);
                      setError("");
                    }}
                    variant="outlined"
                  >
                    Review
                  </Button>
                  <Button onClick={() => setDesigner(t.id)} variant="outlined">
                    Design
                  </Button>
                  <Button
                    disabled={
                      !!busy ||
                      !t.is_active ||
                      (!t.published_version && !t.legacy_unversioned)
                    }
                    onClick={() => {
                      setUse(t);
                      setCustomer(null);
                      setOpportunity(null);
                      setError("");
                      requestId.current = crypto.randomUUID();
                    }}
                  >
                    Use template
                  </Button>
                  <Button variant="plain" onClick={() => openEdit(t)}>
                    Edit settings
                  </Button>
                  <Button
                    variant="plain"
                    disabled={!!busy}
                    onClick={() =>
                      run("duplicate", async () => {
                        const r = await api.post<{ id: number }>(
                          `/api/crm/contract-templates/${t.id}/duplicate/`,
                          {},
                        );
                        setReview(r.data.id);
                      })
                    }
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="plain"
                    disabled={!!busy}
                    onClick={() =>
                      run("active", () =>
                        api.patch(`/api/crm/contract-templates/${t.id}/`, {
                          is_active: !t.is_active,
                        }),
                      )
                    }
                  >
                    {t.is_active ? "Deactivate" : "Reactivate"}
                  </Button>
                  <Button
                    variant="plain"
                    color="danger"
                    onClick={() => {
                      setRemove(t);
                      setError("");
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Card>
            ))}
          </Box>
          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Typography>
              Page {page} of{" "}
              {Math.max(1, Math.ceil((listing.data?.count || 0) / 20))}
            </Typography>
            <Button
              disabled={page * 20 >= (listing.data?.count || 0)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </>
      )}
      {edit !== false && (
        <Modal
          open
          onClose={() => {
            if (!busy) setEdit(false);
          }}
        >
          <ModalDialog
            sx={{
              width: "min(600px,95vw)",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Typography level="h3">
              {edit ? "Edit template settings" : "New template"}
            </Typography>
            {error && <Alert color="danger">{error}</Alert>}
            {(
              ["name", "description", "category", "tags", "roles"] as const
            ).map((key) => (
              <FormControl key={key}>
                <FormLabel>
                  {key === "roles"
                    ? "Recipient roles (comma separated)"
                    : key === "tags"
                      ? "Tags (comma separated)"
                      : key}
                </FormLabel>
                <Input
                  value={form[key]}
                  onChange={(e) =>
                    setForm((old) => ({ ...old, [key]: e.target.value }))
                  }
                />
              </FormControl>
            ))}
            <Select
              value={form.use_signing_order ? "sequential" : "parallel"}
              onChange={(_, v) =>
                setForm((old) => ({
                  ...old,
                  use_signing_order: v === "sequential",
                }))
              }
            >
              <Option value="sequential">Sequential signing</Option>
              <Option value="parallel">Parallel signing</Option>
            </Select>
            <FormControl>
              <FormLabel>Link expiration days</FormLabel>
              <Input
                type="number"
                value={form.link_expiration_days}
                onChange={(e) =>
                  setForm((old) => ({
                    ...old,
                    link_expiration_days: Number(e.target.value),
                  }))
                }
              />
            </FormControl>
            <Stack direction="row" gap={1}>
              <Button
                disabled={!!busy}
                variant="plain"
                onClick={() => setEdit(false)}
              >
                Cancel
              </Button>
              <Button
                loading={busy === "save"}
                disabled={!!busy || !form.name.trim()}
                onClick={() =>
                  run("save", async () => {
                    const data = {
                      ...form,
                      tags: form.tags
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                      default_recipients: form.roles
                        .split(",")
                        .map((role, i) => ({
                          role: role.trim(),
                          signing_order: i + 1,
                        }))
                        .filter((r) => r.role),
                    };
                    if (edit)
                      await api.patch(
                        `/api/crm/contract-templates/${edit.id}/`,
                        data,
                      );
                    else await api.post("/api/crm/contract-templates/", data);
                    setEdit(false);
                  })
                }
              >
                Save settings
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      )}
      {remove && (
        <Modal
          open
          onClose={() => {
            if (!busy) setRemove(null);
          }}
        >
          <ModalDialog>
            <Typography level="h3">Delete {remove.name}?</Typography>
            <Typography>
              Templates used by contracts must be deactivated to preserve their
              versions.
            </Typography>
            {error && <Alert color="danger">{error}</Alert>}
            <Button disabled={!!busy} onClick={() => setRemove(null)}>
              Cancel
            </Button>
            <Button
              color="danger"
              loading={busy === "delete"}
              onClick={() =>
                run("delete", async () => {
                  await api.delete(`/api/crm/contract-templates/${remove.id}/`);
                  setRemove(null);
                })
              }
            >
              Delete template
            </Button>
          </ModalDialog>
        </Modal>
      )}
      {use && (
        <Modal
          open
          onClose={() => {
            if (!busy) setUse(null);
          }}
        >
          <ModalDialog sx={{ width: "min(650px,95vw)" }}>
            <Typography level="h3">Create contract from {use.name}</Typography>
            <Typography>
              {use.published_version
                ? `Uses published version ${use.published_version}.`
                : "The existing template will be checked and versioned before creation."}
            </Typography>
            {error && <Alert color="danger">{error}</Alert>}
            <Autocomplete
              placeholder="Search customer"
              value={customer}
              options={contacts.data?.results || contacts.data || []}
              getOptionLabel={(c: any) =>
                `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email
              }
              isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
              loading={contacts.isFetching}
              onInputChange={(_, text, reason) => {
                if (reason === "input") setContactSearch(text);
              }}
              onChange={(_, value) => {
                setCustomer(value);
                setOpportunity(null);
                requestId.current = crypto.randomUUID();
              }}
            />
            <Autocomplete
              key={customer?.id || "none"}
              placeholder="Opportunity (optional)"
              value={opportunity}
              options={
                customer
                  ? opportunities.data?.results || opportunities.data || []
                  : []
              }
              getOptionLabel={(o: any) => o.name}
              disabled={!customer || opportunities.isFetching}
              loading={opportunities.isFetching}
              onChange={(_, value) => {
                setOpportunity(value);
                requestId.current = crypto.randomUUID();
              }}
            />
            {opportunities.isError && (
              <Alert color="warning">
                Opportunities unavailable.{" "}
                <Button onClick={() => opportunities.refetch()}>Retry</Button>
              </Alert>
            )}
            <Stack direction="row" gap={1}>
              <Button
                disabled={!!busy}
                onClick={() => setUse(null)}
                variant="plain"
              >
                Cancel
              </Button>
              <Button
                loading={busy === "create"}
                disabled={!!busy || !customer}
                onClick={() =>
                  run("create", async () => {
                    const r = await api.post<{ id: number }>(
                      `/api/crm/contract-templates/${use.id}/use_template/`,
                      {
                        customer_id: customer.id,
                        opportunity_id: opportunity?.id || null,
                        request_id: requestId.current,
                      },
                    );
                    setUse(null);
                    onUseTemplate?.(r.data.id);
                  })
                }
              >
                Create contract
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      )}
      {categoryModal && (
        <Modal
          open
          onClose={() => {
            if (!busy) setCategoryModal(false);
          }}
        >
          <ModalDialog>
            <Typography level="h3">Manage categories</Typography>
            {error && <Alert color="danger">{error}</Alert>}
            <Select
              value={previousCategory}
              onChange={(_, value) => setPreviousCategory(value || "")}
            >
              <Option value="">Create a category</Option>
              {categories.data?.categories.map((c) => (
                <Option key={c} value={c}>
                  Rename {c}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
            <Button
              disabled={!!busy || !categoryName.trim()}
              onClick={() =>
                run("category", async () => {
                  await api.post("/api/crm/contract-templates/categories/", {
                    name: categoryName,
                    previous: previousCategory,
                  });
                  setCategoryModal(false);
                })
              }
            >
              Save category
            </Button>
            <Button
              disabled={!!busy}
              variant="plain"
              onClick={() => setCategoryModal(false)}
            >
              Close
            </Button>
          </ModalDialog>
        </Modal>
      )}
      {review && (
        <TemplateReview
          key={review}
          id={review}
          close={() => setReview(null)}
        />
      )}
      {designer && (
        <Modal open onClose={() => setDesigner(null)}>
          <ModalDialog
            sx={{
              width: "95vw",
              height: "95vh",
              maxWidth: 1500,
              overflow: "auto",
            }}
          >
            <TemplateDesigner
              key={designer}
              templateId={designer}
              onSave={() => {
                refresh();
                setDesigner(null);
              }}
              onCancel={() => setDesigner(null)}
            />
          </ModalDialog>
        </Modal>
      )}
    </Box>
  );
};
